import type {
    Experiment,
    Pointers,
    SchemaService,
    UndoRedoConfig,
    Events,
    ValidationService,
    FragmentListItem,
    SupportedFragments
} from '@wix/document-services-types'
import {EventEmitter} from 'events'
import _ from 'lodash'
import {createDal, type CustomGetter, PAGE_FRAGMENT_TYPE, type DAL} from './dal/documentManagerDal'
import {createResourceLoader, ResourceLoaderAPI} from './resourceLoader'
import type {DocumentAPI} from '@wix/document-api-types'
import type {
    CoreLogger,
    DmApis,
    DMApisWithEvents,
    DocumentDataTypes,
    Extension,
    ExtensionAPI,
    ExtensionPublicAPI,
    PostSetOperation,
    ValidatorScope
} from './types'
import {getPointer} from './utils/pointerUtils'
import {createFragmentStore} from './dal/fragment/fragmentStore'
import {createOrchestrator, type Orchestrator} from './orchestrator'

const assignWithoutDuplicates = (target: Record<string, any>, sources: Record<string, any>, type: string) => {
    _.forOwn(sources, (value, key) => {
        if (target[key]) {
            throw new Error(`Trying to add an existing "${type}" with key "${key}"`)
        }

        target[key] = value
    })
}

export interface CoreConfig {
    experimentInstance: Experiment
    logger: CoreLogger
    tracking?: Function
    schemaService: SchemaService
    validationService?: ValidationService
    transactionActionsLimit: number
    signatureSeed?: string
    undoRedoConfig?: UndoRedoConfig
    disableCommonConfig?: boolean
    dontCollectFixerVersionData?: boolean
    supportsUsingPresetVariants?: boolean
    minNumOfPagesToCalculateRenderHints: number
    enableWidgetInRepeater?: boolean
    supportedFragments?: SupportedFragments
    withoutViewer?: boolean
    enforceStrictValidationEverywhere?: boolean
    enforceNoWhitelistDalStrictSchemaValidation?: boolean
    strictBlacklistBaselineForEmployees?: boolean
    loadingPartialPages?: boolean
}

export interface ModelsInitializationType {
    rendererModel: any
    documentServicesModel: any & {
        fragmentsList?: FragmentListItem[]
    }
    serviceTopology: any
}
export interface DocumentManager {
    dal: DAL
    resourceLoader: ResourceLoaderAPI
    pointers: Pointers
    extensionAPI: ExtensionAPI
    publicAPI: ExtensionPublicAPI
    documentAPI: DocumentAPI
    EVENTS: Record<string, any>
    eventEmitter: EventEmitter
    events: Events
    registerExtension(ext: Extension): void
    initialize(): Promise<void>
    initializeAfterDataLoaded(): Promise<void>
    initializeChannelSubscriptions(): Promise<void>
    initializeModels(initData: ModelsInitializationType): void
    orchestrator: Orchestrator
    experimentInstance: Experiment
    logger: CoreLogger
    schemaService: any
    config: CoreConfig
}

export const createDMCore = (coreConfig: CoreConfig): DocumentManager => {
    const {experimentInstance, logger, schemaService} = coreConfig
    const orchestrator = createOrchestrator()
    const extensions: Extension[] = []
    const postSetOperations: PostSetOperation[] = []
    const customGetters: Record<string, CustomGetter> = {}
    const dmTypes: DocumentDataTypes = {}
    const pointers: Pointers = {} as Pointers
    const extensionAPI: ExtensionAPI = {}
    const publicAPI: ExtensionPublicAPI = {}
    const documentAPI: DocumentAPI = {} as DocumentAPI
    const eventEmitter: EventEmitter = new EventEmitter()
    //Some events/hooks in our system are expected to have many listeners.
    eventEmitter.setMaxListeners(20)
    const EVENTS = {}
    const events: Events = {
        emitter: eventEmitter
    }
    const tracking = coreConfig.tracking ?? (async (name: string, fn: Function) => await fn())
    const resourceLoader = createResourceLoader(coreConfig)
    const dalArgs = {coreConfig, postSetOperations, dmTypes, customGetters, eventEmitter}
    const dal: DAL = createDal(dalArgs)
    const dmApis: DmApis = {coreConfig, dal, pointers, extensionAPI, EVENTS, resourceLoader}
    const dmAPIsWithEvents: DMApisWithEvents = {...dmApis, eventEmitter}
    const validate = (extension: Extension): void => {
        if (extension.name) {
            if (_(extensions).map('name').includes(extension.name)) {
                throw new Error(`extension name should be unique ${extension.name}`)
            }
        } else {
            throw new Error('extension should have a name')
        }
    }

    const registerExtension = (extension: Extension): void => {
        validate(extension)
        extensions.push(extension)

        assignWithoutDuplicates(customGetters, _.invoke(extension, ['createGetters'], dmApis), 'customGetter')
        _.merge(dmTypes, _.invoke(extension, ['getDocumentDataTypes'], dmApis))
        _.merge(pointers, _.invoke(extension, ['createPointersMethods'], dmApis))
        _.merge(extensionAPI, _.invoke(extension, ['createExtensionAPI'], dmAPIsWithEvents))
        _.merge(publicAPI, _.invoke(extension, ['createPublicAPI'], dmAPIsWithEvents))
        const extensionServiceAPI = _.invoke(extension, ['createServiceAPI'], dmAPIsWithEvents)
        if (extensionServiceAPI) {
            assignWithoutDuplicates(documentAPI, orchestrator.wrapServiceAPIEntry(extensionServiceAPI), 'documentAPI')
        }
        _.merge(EVENTS, extension.EVENTS)
        _.merge(events, extension.EVENTS)

        const filters = _.invoke(extension, ['createFilters'], dmApis)
        _.forEach(filters, (filter, name) => dal.registrar.registerFilter(name, filter))

        const defaultValidatorScopes: ValidatorScope = {
            include: [PAGE_FRAGMENT_TYPE],
            exclude: []
        }
        const validators = _.invoke(extension, ['createValidator'], dmApis)
        _.forEach(validators, (validator, name) =>
            dal.registrar.registerValidator(name, validator, extension.validatorScope ?? defaultValidatorScopes)
        )

        const rebaseValidators = _.invoke(extension, ['createRebaseValidator'], dmApis)
        _.forEach(rebaseValidators, (validator, name) => dal.registrar.registerRebaseValidator(name, validator))

        const postTransactionOperations = _.invoke(extension, ['createPostTransactionOperations'], {
            ...dmApis,
            eventEmitter
        })
        _.forEach(postTransactionOperations, (op, name) => dal.registrar.registerPostTransactionOperation(name, op))

        const transactionDerivedMetadata = _.invoke(extension, ['createTransactionDerivedMetadata'], {
            ...dmApis,
            eventEmitter
        })

        if (transactionDerivedMetadata) {
            _.forEach(transactionDerivedMetadata, ([op, fragmentType], name) =>
                dal.registrar.registerTransactionDerivedMetadata(name, op, fragmentType)
            )
        }

        const changesListener = _.invoke(extension, ['createChangesListener'], dmApis)
        if (changesListener) {
            dal.registrar.registerForChangesCallback(changesListener)
        }

        if (extension.createPostSetOperation) {
            postSetOperations.push(extension.createPostSetOperation(dmApis))
        }

        if (extension.createResourceLoader) {
            const resourceLoaderDefinitions = extension.createResourceLoader(dmAPIsWithEvents)
            _.forEach(resourceLoaderDefinitions, loaderDefinition =>
                resourceLoader.registerResourceLoader(loaderDefinition)
            )
        }
        if (extension.initialState) {
            const initialTypeState = createFragmentStore()
            _.forEach(extension.initialState, (state, type) => {
                _.forEach(state, (value, key) => {
                    initialTypeState.set(getPointer(key, type), value)
                })
            })
            dal.mergeToApprovedStore(initialTypeState, extension.name)
        }

        if (extension.subscribeToEvents) {
            extension.subscribeToEvents(dmAPIsWithEvents)
        }
    }
    const invokeAsyncOnExtensionsAndTrack = (
        fnName: keyof Extension,
        extensionArgs: any[],
        invocationCallback?: (extensionName: string) => void
    ): any[] => {
        const timings: Record<string, number> = {}
        const totalStart = performance.now()
        const results = extensions
            .filter(ex => ex[fnName])
            .map(ex => {
                const start = performance.now()
                const returnValue = tracking(`${fnName}-${ex.name}`, () => _.invoke(ex, fnName, ...extensionArgs))
                timings[ex.name] = performance.now() - start
                invocationCallback?.(ex.name)
                return returnValue
            })
        const totalDuration = performance.now() - totalStart
        logger.interactionEnded(`dm-extensions-${fnName}`, {extras: {totalDuration, timings}})
        return results
    }

    const initialize = async () => {
        await Promise.all(
            invokeAsyncOnExtensionsAndTrack('initialize', [dmAPIsWithEvents], extensionName => {
                dal.safeCommitTransactionWithErrorReport(
                    {
                        failedValidationAfterEarlyBaseline: true,
                        phase: 'extensions',
                        extensionName
                    },
                    `after-${extensionName}`
                )
            })
        )
    }

    const initializeAfterDataLoaded = async () => {
        await Promise.all(
            invokeAsyncOnExtensionsAndTrack('initializeAfterDataLoaded', [dmAPIsWithEvents], extensionName => {
                dal.safeCommitTransactionWithErrorReport(
                    {
                        failedValidationAfterEarlyBaseline: true,
                        phase: 'extensions',
                        extensionName
                    },
                    `after-${extensionName}-data-loaded`
                )
            })
        )
    }

    const initializeChannelSubscriptions = async () => {
        await Promise.all(invokeAsyncOnExtensionsAndTrack('initializeChannelSubscriptions', [dmAPIsWithEvents]))
    }
    const initializeModels = (initModelsData: ModelsInitializationType) => {
        const initialModelsState = createFragmentStore()
        const timings: Record<string, number> = {}
        const totalStart = performance.now()
        extensions
            .filter(ex => ex.initializeModels)
            .forEach(ex => {
                const start = performance.now()
                _.invoke(ex, ['initializeModels'], initialModelsState, initModelsData, dmAPIsWithEvents)
                timings[ex.name] = performance.now() - start
            })
        const totalDuration = performance.now() - totalStart
        logger.interactionEnded('dm-extensions-initializeModels', {extras: {totalDuration, timings}})
        dal.mergeToApprovedStore(initialModelsState, 'model-initializer')
    }

    return {
        registerExtension,
        initialize,
        initializeAfterDataLoaded,
        initializeModels,
        initializeChannelSubscriptions,
        resourceLoader,
        extensionAPI,
        publicAPI,
        documentAPI,
        dal,
        eventEmitter,
        EVENTS,
        events,
        pointers,
        orchestrator,
        experimentInstance,
        logger,
        schemaService,
        config: coreConfig
    }
}
